import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../axiosSetup";
import "bootstrap/dist/css/bootstrap.min.css";
import CustomNavbar from "../CustomNavbar";
import ZRPersonnelFileList from './zrPersonnelFileList';
import cfg from '../../config';

const formatDateForInput = (dateString) => {
  const parts = dateString.split(".");
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
  }
  return dateString; // Falls das Format unerwartet ist, einfach zurückgeben
};

const MemberEdit = () => {
  const location = useLocation();
  const { state } = useLocation();
  const member = state ? state.member : null;
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    birthDate: "",
    street: "",
    houseNumber: "",
    gesperrt: "",
    city: "",
    iban: "",
    activated: false,
    zunftrat: false,
    password: "",
    confirmPassword: ""
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const navigate = useNavigate();

  // useEffect zum Laden der Daten von der API
  useEffect(() => {
    const loadMemberData = async () => {
      if (member) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await api.get(`/api/specific-member-zunftrat/${member.ID}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const memberData = response.data[0];
  
          setFormData({
            memberID: memberData.ID || "",
            firstName: memberData.firstName || "",
            lastName: memberData.lastName || "",
            email: memberData.email || "",
            birthDate: memberData.birthDate ? formatDateForInput(memberData.birthDate) : "",
            street: memberData.street || "",
            houseNumber: memberData.houseNumber || "",
            gesperrt: memberData.isBlockedUntill ? formatDateForInput(memberData.isBlockedUntill) : "",
            city: memberData.city || "",
            iban: memberData.IBAN || "",
            activated: memberData.isActivated === 1 || memberData.isActivated === "Ja",
            zunftrat: memberData.isZunftrat === 1 || memberData.isZunftrat === "Ja",

            password: "",
            confirmPassword: "",
          });
        } catch (err) {
          setError("Fehler beim Laden der Stammdaten: " + err.message);
        }
      }
    };
  
    // 🔐 Prüfe Adminstatus aus localStorage
    const userData = localStorage.getItem("isZunftratAdmin");
    console.debug("Meine Nuterdaten: ", userData);
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
       
        if (parsedUser === 1 || parsedUser === true) {
          setIsAdmin(true);
        }
      } catch {
        console.warn("⚠️ Benutzerinfo im localStorage konnte nicht gelesen werden.");
      }
    }
  
    loadMemberData();
  }, [member]);
  

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      let response;
      if (member) {
        response = await api.put(`/api/edit-member-zunftrat`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-member", { state: { message: response.data.message } });
      } else {
        response = await api.post("/api/create-member-zunftrat", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-member", { state: { message: response.data.message } });
      }
    } catch (err) {
      setError(`Fehler beim Speichern (${cfg.memberTerm}): ` + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!member || !member.ID) return;

    const confirmed = window.confirm(`Möchtest du diesen Eintrag wirklich löschen (${cfg.memberTerm})?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      await api.delete(`/api/delete-member-zunftrat/${member.ID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/zr-member", { state: { message: `${cfg.memberTerm} erfolgreich gelöscht!` } });
    } catch (err) {
      setError(`Fehler beim Löschen (${cfg.memberTerm}): ` + err.message);
    }
  };

  return (
    <div>
      <CustomNavbar />
      {location.state && location.state.message && (
          <div className="alert alert-warning">
              {location.state.message}
          </div>
      )}
      <div className="container mt-4">
        <h2 className="mb-4 text-center">{member ? `${cfg.memberTerm} bearbeiten` : `${cfg.memberTerm} erstellen`}</h2>

        <form onSubmit={handleSubmit}>
          {/* Formularfelder */}
          <div className="mb-3">
            <label htmlFor="firstName" className="form-label">Vorname</label>
            <input type="text" className="form-control" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="lastName" className="form-label">Nachname</label>
            <input type="text" className="form-control" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="email" className="form-label">E-Mail</label>
            <input type="email" className="form-control" id="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="birthDate" className="form-label">Geburtsdatum</label>
            <input type="date" className="form-control" id="birthDate" name="birthDate" value={formData.birthDate} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="street" className="form-label">Straße</label>
            <input type="text" className="form-control" id="street" name="street" value={formData.street} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="houseNumber" className="form-label">Hausnummer</label>
            <input type="text" className="form-control" id="houseNumber" name="houseNumber" value={formData.houseNumber} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="city" className="form-label">Ort</label>
            <input type="text" className="form-control" id="city" name="city" value={formData.city} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="iban" className="form-label">IBAN</label>
            <input type="text" className="form-control" id="iban" name="iban" value={formData.iban} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label">Gesperrt</label>
            <input
              type="date"
              className="form-control"
              name="gesperrt"
              value={formData.gesperrt}
              onChange={handleChange}
              disabled="true"
            />
          </div>
          <div className="mb-3 form-check">
            <input type="checkbox" className="form-check-input" id="activated" name="activated" checked={formData.activated} onChange={handleChange} />
            <label className="form-check-label" htmlFor="activated">Aktiviert</label>
          </div>
          {isAdmin && (
            <>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="zunftrat"
                  name="zunftrat"
                  checked={formData.zunftrat}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="zunftrat">{cfg.adminRoleName}</label>
              </div>

            </>
          )}

          {/* Passwortfelder */}
          <div className="mb-3">
            <label htmlFor="password" className="form-label">Passwort</label>
            <input
              type="password"
              className="form-control"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}

            />
          </div>
          <div className="mb-3">
            <label htmlFor="confirmPassword" className="form-label">Passwort bestätigen</label>
            <input
              type="password"
              className="form-control"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>

          {/* Fehlermeldungen */}
          {error && <div className="alert alert-danger">{error}</div>}

          <button type="submit" className="btn btn-primary">{loading ? "Speichern..." : "Speichern"}</button>
        </form>

        {member && (
          <button className="btn btn-danger mt-3" onClick={handleDelete}>
            {cfg.memberTerm} löschen
          </button>
        )}
      </div>
      <br/>
      <br/>
      {member && <ZRPersonnelFileList  memberID={member.ID} />}
      <br/>
      <br/>
    </div>
  );
};

export default MemberEdit;
